import { useState, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { FileText, Clock, ChevronRight, Search, X } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { quotesApi, QuoteRequest } from '../api/quotes';

type StatusFilter = 'all' | 'pending' | 'accepted' | 'rejected' | 'reviewed';

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-amber-50 text-amber-700',
  reviewed: 'bg-blue-50 text-blue-700',
  accepted: 'bg-emerald-50 text-emerald-700',
  rejected: 'bg-red-50 text-red-700',
};

const STATUS_LABELS: Record<string, { fr: string; en: string }> = {
  pending: { fr: 'En attente', en: 'Pending' },
  reviewed: { fr: 'En cours de révision', en: 'Under review' },
  accepted: { fr: 'Accepté', en: 'Accepted' },
  rejected: { fr: 'Refusé', en: 'Rejected' },
};

export default function ResellerQuotes() {
  usePageTitle('Mes devis — Lodiss');

  const { lang, setPage, setSelectedQuoteId } = useApp();

  const [quotes, setQuotes] = useState<QuoteRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  useEffect(() => {
    quotesApi.getQuotes()
      .then(setQuotes)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const getStatusColor = (s: string) => STATUS_COLORS[s] ?? 'bg-gray-50 text-gray-700';
  const getStatusLabel = (s: string) => STATUS_LABELS[s]?.[lang] ?? s;

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', {
      day: 'numeric', month: 'long', year: 'numeric',
    });

  // ── Smart keyword filtering ──────────────────────────────────────────────────
  const parseToken = (token: string, q: QuoteRequest): boolean => {
    const t = token.toLowerCase();

    // Universal keywords — match all devis
    if (t === 'lodiss' || t === 'devis') return true;

    // "#N" → match by id
    const hashMatch = t.match(/^#(\d+)$/);
    if (hashMatch) return q.id === parseInt(hashMatch[1], 10);

    // Pure number → match by id
    if (/^\d+$/.test(t)) return q.id === parseInt(t, 10);

    // Status keywords (FR + EN)
    if (t === 'pending' || t === 'attente') return q.status === 'pending';
    if (t === 'accepted' || t === 'accepté') return q.status === 'accepted';
    if (t === 'rejected' || t === 'refusé') return q.status === 'rejected';
    if (t === 'reviewed' || t === 'révision') return q.status === 'reviewed';

    // Date: YYYY-MM-DD or DD/MM/YYYY
    const isoDate = t.match(/^(\d{4}-\d{2}-\d{2})$/);
    if (isoDate) return q.created_at.startsWith(isoDate[1]);
    const frDate = t.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (frDate) return q.created_at.startsWith(`${frDate[3]}-${frDate[2]}-${frDate[1]}`);

    // Fallback: substring match on id
    return String(q.id).includes(t);
  };

  const filtered = quotes.filter(q => {
    // Quick-filter chip (AND with text search)
    if (statusFilter !== 'all' && q.status !== statusFilter) return false;

    const trimmed = search.trim();
    if (!trimmed) return true;

    // Tokenise and AND all tokens
    const tokens = trimmed.split(/\s+/);
    return tokens.every(tok => parseToken(tok, q));
  });

  // ── Hint text under search ────────────────────────────────────────────────────
  const searchHint = search.trim()
    ? `${filtered.length} ${lang === 'fr' ? 'résultat(s) pour' : 'result(s) for'} "${search.trim()}"`
    : `${lang === 'fr' ? 'Tous les devis' : 'All quotes'} (${quotes.length} ${lang === 'fr' ? 'total' : 'total'})`;

  // ── Quick-filter buttons config ───────────────────────────────────────────────
  const FILTERS: { key: StatusFilter; fr: string; en: string }[] = [
    { key: 'all', fr: 'Tous', en: 'All' },
    { key: 'pending', fr: 'En attente', en: 'Pending' },
    { key: 'accepted', fr: 'Accepté', en: 'Accepted' },
    { key: 'rejected', fr: 'Refusé', en: 'Rejected' },
  ];

  // ── Navigate to detail ────────────────────────────────────────────────────────
  const goToDetail = (id: number) => {
    setSelectedQuoteId(id);
    setPage('reseller-quote-detail');
  };

  // ── Loading ───────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <main className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-lodiss-red border-t-transparent rounded-full animate-spin" />
      </main>
    );
  }

  return (
    <main className="bg-gray-50 min-h-screen py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ── Page title ──────────────────────────────────────────────────────── */}
        <h1 className="text-2xl font-bold text-gray-950 mb-6">
          {lang === 'fr' ? 'Mes demandes de devis' : 'My quote requests'}
        </h1>

        {quotes.length === 0 ? (
          /* ── Empty state ────────────────────────────────────────────────────── */
          <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
            <FileText size={40} className="mx-auto text-gray-200 mb-3" />
            <p className="text-gray-400 text-sm mb-5">
              {lang === 'fr' ? 'Aucune demande de devis' : 'No quote requests'}
            </p>
            <button
              onClick={() => setPage('reseller-catalogue')}
              className="px-5 py-2.5 bg-lodiss-red hover:bg-lodiss-dark text-white text-sm font-semibold rounded-lg transition-colors"
            >
              {lang === 'fr' ? 'Demander un devis' : 'Request a quote'}
            </button>
          </div>
        ) : (
          <>
            {/* ── Search + quick filters ─────────────────────────────────────── */}
            <div className="bg-white rounded-xl border border-gray-100 p-4 mb-5 space-y-3">
              {/* Search input */}
              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder={lang === 'fr' ? 'Filtrer par numéro, statut ou date…' : 'Filter by number, status or date…'}
                  className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-lodiss-red focus:ring-1 focus:ring-lodiss-red/20 transition-colors"
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X size={13} />
                  </button>
                )}
              </div>

              {/* Quick-filter chips */}
              <div className="flex flex-wrap gap-2">
                {FILTERS.map(f => (
                  <button
                    key={f.key}
                    onClick={() => setStatusFilter(f.key)}
                    className={`px-3 py-1 text-xs font-semibold rounded-full border transition-colors ${statusFilter === f.key
                      ? 'bg-lodiss-red text-white border-lodiss-red'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-lodiss-red hover:text-lodiss-red'
                      }`}
                  >
                    {lang === 'fr' ? f.fr : f.en}
                  </button>
                ))}
                <span className="ml-auto text-xs text-gray-400 self-center">
                  {searchHint}
                </span>
              </div>
            </div>

            {/* ── Quotes list ────────────────────────────────────────────────── */}
            {filtered.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-100 p-10 text-center">
                <Search size={28} className="mx-auto text-gray-200 mb-3" />
                <p className="text-sm text-gray-400">
                  {lang === 'fr' ? 'Aucun devis ne correspond à votre recherche.' : 'No quotes match your search.'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filtered.map(quote => (
                  <div
                    key={quote.id}
                    className="bg-white rounded-xl border border-gray-100 p-5 hover:border-gray-200 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-3">
                      {/* Left: icon + id + badge */}
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                          <FileText size={20} className="text-amber-600" />
                        </div>
                        <div className="min-w-0">
                          <span className="text-sm font-bold text-gray-900">
                            {lang === 'fr' ? 'Devis' : 'Quote'} #{quote.id}
                          </span>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${getStatusColor(quote.status)}`}>
                              {getStatusLabel(quote.status)}
                            </span>
                            <span className="flex items-center gap-1 text-xs text-gray-400">
                              <Clock size={11} />
                              {formatDate(quote.created_at)}
                            </span>
                            <span className="text-xs text-gray-400">
                              · {quote.items.length} {lang === 'fr' ? 'article(s)' : 'item(s)'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Right: detail button */}
                      <button
                        onClick={() => goToDetail(quote.id)}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-gray-500 border border-gray-200 rounded-lg hover:border-lodiss-red hover:text-lodiss-red transition-colors flex-shrink-0"
                        title={lang === 'fr' ? 'Voir le détail' : 'View detail'}
                      >
                        {lang === 'fr' ? 'Détail' : 'Detail'}
                        <ChevronRight size={13} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}
